import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { InputText } from "primereact/inputtext";

import { ApplicationState } from "../../../store";
import { services } from "../../../services";
import { alertError, alertSuccess } from "../../../store/reducers/Alert";
import { getUserContext } from "../../../store/reducers/UserContext";
import { useBackgroundPromiseWrapper } from "../../../helpers/hooks/useBackgroundPromiseWrapper";
import { PageHeading } from "../../../components/synced/ui/PageHeading/PageHeading";
import { IconBadge } from "../../../components/synced/ui/IconBadge/IconBadge";
import { FormField } from "../../../components/shared/FormField/FormField";

import cf from "../../../styles/card-form.module.css";
import styles from "./ProfilePage.module.css";

export const ProfilePage: React.FC = () => {
  const dispatch = useDispatch();
  const userContext = useSelector((appState: ApplicationState) => appState.context.userContext);
  const [submit, isLoading] = useBackgroundPromiseWrapper();
  const [inn, setInn] = React.useState("");
  const [kpp, setKpp] = React.useState("");
  const [sapCodeRows, setSapCodeRows] = React.useState<string[]>([""]);
  const [errorKeys, setErrorKeys] = React.useState<Set<string>>(new Set());
  const [saved, setSaved] = React.useState(false);
  const isChildAccount = userContext.OwnerId != null;

  const sapCodes = React.useMemo(
    () => sapCodeRows
      .map(x => x.replace(/\D/g, ""))
      .filter(Boolean)
      .map(x => x.padStart(10, "0")),
    [sapCodeRows]
  );

  React.useEffect(() => {
    setInn(userContext.Inn ?? "");
    setKpp(userContext.Kpp ?? "");
    setSapCodeRows((userContext.SapCodes?.length ? userContext.SapCodes : [""]));
  }, [userContext.Inn, userContext.Kpp, userContext.SapCodes]);

  const updateSapCode = (index: number, value: string) => {
    if (isChildAccount) return;
    setSapCodeRows(prev => prev.map((code, i) => i === index ? value.replace(/\D/g, "") : code));
  };

  const addSapCode = () => {
    if (isChildAccount) return;
    setSapCodeRows(prev => [...prev, ""]);
  };

  const removeSapCode = (index: number) => {
    if (isChildAccount) return;
    setSapCodeRows(prev => {
      const next = prev.filter((_, i) => i !== index);
      return next.length > 0 ? next : [""];
    });
  };

  const validate = () => {
    const errors = new Set<string>();
    if (![10, 12].includes(inn.length)) errors.add("Inn");
    if (kpp.length !== 9) errors.add("Kpp");
    if (sapCodes.length === 0) {
      errors.add("SapCodes");
      dispatch(alertError({ detail: "Укажите хотя бы один SAP-код" }));
    }
    setErrorKeys(errors);
    return errors.size === 0;
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isChildAccount) return;
    if (!validate()) return;

    submit(
      () => services.userService.updateSapCodes({ Inn: inn, Kpp: kpp, SapCodes: sapCodes }),
      () => {
        dispatch(alertSuccess({ detail: "SAP-коды сохранены" }));
        services.userService.getCurrentUserContext().then(res => {
          dispatch(getUserContext(res.data));
          setSaved(true);
          setTimeout(() => setSaved(false), 2200);
        });
      }
    );
  };

  const onCancel = () => {
    setInn(userContext.Inn ?? "");
    setKpp(userContext.Kpp ?? "");
    setSapCodeRows(userContext.SapCodes?.length ? userContext.SapCodes : [""]);
    setErrorKeys(new Set());
  };

  const displayName = userContext.Title || userContext.Login || "";
  const email = userContext.Email || "";
  const initial = (email[0] || "U").toUpperCase();

  const inputClass = (field: string) =>
    `${cf.input}${errorKeys.has(field) ? ` ${cf.inputError}` : ""}${isChildAccount ? ` ${cf.inputDisabled}` : ""}`;

  return (
    <div className={cf.root}>
      <PageHeading
        initial={initial}
        title="Профиль"
        subtitle="Данные организации и доступ к разделам портала"
      />

      <form onSubmit={onSubmit} noValidate className={cf.card}>
        <div className={`${styles.banner}${isChildAccount ? ` ${styles.bannerChild}` : ""}`}>
          <i className={`fa-solid fa-circle-info ${styles.bannerIcon}`} />
          <p className={styles.bannerText}>
            {isChildAccount
              ? "ИНН, КПП и SAP-коды наследуются от родительского аккаунта."
              : "Для доступа к разделам укажите SAP-коды вашей организации. Коды будут проверены по ИНН и КПП."}
          </p>
        </div>

        <div className={cf.cardBody}>
          <div className={styles.identityTiles}>
            <div className={styles.identityTile}>
              <IconBadge icon="fa-regular fa-user" size={36} bg="#eef1fc" color="#5566ad" />
              <div>
                <div className={styles.tileLabel}>Пользователь</div>
                <div className={styles.tileValue}>{displayName}</div>
              </div>
            </div>
            <div className={styles.identityTile}>
              <IconBadge icon="fa-regular fa-envelope" size={36} bg="#eef7f0" color="#3f7a52" />
              <div>
                <div className={styles.tileLabel}>Email</div>
                <div className={styles.tileValue}>{email}</div>
              </div>
            </div>
          </div>

          <div className={cf.fieldRow}>
            <FormField
              label="ИНН"
              hint="10 цифр"
              error={errorKeys.has("Inn") ? "ИНН должен содержать 10 или 12 цифр" : undefined}
              className={cf.fieldItem}
            >
              <InputText
                value={inn}
                maxLength={12}
                className={inputClass("Inn")}
                disabled={isChildAccount}
                placeholder="0000000000"
                onChange={e => setInn((e.target as HTMLInputElement).value.replace(/\D/g, ""))}
              />
            </FormField>
            <FormField
              label="КПП"
              hint="9 цифр"
              error={errorKeys.has("Kpp") ? "КПП должен содержать 9 цифр" : undefined}
              className={cf.fieldItem}
            >
              <InputText
                value={kpp}
                maxLength={9}
                className={inputClass("Kpp")}
                disabled={isChildAccount}
                placeholder="000000000"
                onChange={e => setKpp((e.target as HTMLInputElement).value.replace(/\D/g, ""))}
              />
            </FormField>
          </div>

          <div className={styles.sapHeader}>
            <span className={styles.sapLabel}>
              SAP-коды <span className={styles.sapBadge}>{sapCodeRows.length}</span>
            </span>
            {!isChildAccount && (
              <button type="button" className={styles.btnAddCode} onClick={addSapCode}>
                <i className="fa-solid fa-plus" />
                Добавить код
              </button>
            )}
          </div>

          <div className={styles.sapList}>
            {sapCodeRows.length === 0 || (sapCodeRows.length === 1 && !sapCodeRows[0] && errorKeys.has("SapCodes")) ? (
              <div className={styles.sapEmpty}>Пока не добавлено ни одного SAP-кода</div>
            ) : null}

            {sapCodeRows.map((sapCode, index) => (
              <div className={styles.sapRow} key={index}>
                <span className={styles.sapRowNum}>
                  {String(index + 1).padStart(2, "0")}
                </span>
                <InputText
                  value={sapCode}
                  maxLength={10}
                  className={`${styles.sapInput}${errorKeys.has("SapCodes") ? ` ${styles.sapInputError}` : ""}${isChildAccount ? ` ${styles.sapInputDisabled}` : ""}`}
                  disabled={isChildAccount}
                  placeholder="0000000000"
                  onChange={e => updateSapCode(index, (e.target as HTMLInputElement).value)}
                />
                {!isChildAccount && (
                  <button
                    type="button"
                    className={styles.btnRemove}
                    title="Удалить"
                    onClick={() => removeSapCode(index)}
                    disabled={sapCodeRows.length === 1 && !sapCode}
                  >
                    <i className="fa-regular fa-trash-can" />
                  </button>
                )}
              </div>
            ))}
          </div>

          {!isChildAccount && (
            <div className={cf.actions}>
              <button
                type="submit"
                className={cf.btnSave}
                disabled={isLoading}
              >
                {saved ? (
                  <><i className="fa-solid fa-check" /> Сохранено</>
                ) : (
                  <><i className="fa-regular fa-floppy-disk" /> Сохранить</>
                )}
              </button>
              <button
                type="button"
                className={cf.btnBack}
                onClick={onCancel}
              >
                Отмена
              </button>
            </div>
          )}
        </div>
      </form>
    </div>
  );
};
